import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getGuides, trackEvent } from '../lib/api';
import SEO from '../components/SEO';

export default function GuidesIndex() {
  const [guides, setGuides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackEvent({ event_type: 'page_view', page: 'guides_index' });
    getGuides()
      .then(r => { setGuides(r.data.guides || []); setCategories(r.data.categories || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-16" data-testid="guides-index-page">
      <SEO
        title="Burlington Real Estate Guides — Buying, Relocating & Neighbourhood Comparisons"
        description="51 in-depth guides for Burlington homebuyers: city comparisons, budget planning, school guides, commute analysis, neighbourhood rankings, and buyer persona guides."
        canonical="/guides"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Guides', url: '/guides' }]}
      />
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/adwppyfq_1c2ce637-6eec-44f1-8089-a44df9a3c109.png" alt="Burlington waterfront skyline" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600 mb-3 block">Burlington Guides</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4 max-w-3xl" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Everything you need to know about <span className="italic">buying in Burlington</span>
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl leading-relaxed mb-8">
            {guides.length} in-depth guides covering neighbourhoods, pricing, schools, commute, lifestyle, and how Burlington compares to other cities.
          </p>
          <Link to="/blueprint" data-testid="guides-hero-cta">
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-7 h-11 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all">
              Skip to My Personalized Results <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 md:py-20">
        {loading ? (
          <div className="space-y-12">
            {[1,2,3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-6 bg-slate-200 rounded-full w-48 mb-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[1,2,3].map(j => <div key={j} className="h-24 bg-slate-100 rounded-2xl"></div>)}
                </div>
              </div>
            ))}
          </div>
        ) : categories.map(cat => {
          const catPages = guides.filter(p => p.category_slug === cat.slug);
          if (catPages.length === 0) return null;
          return (
            <section key={cat.slug} className="mb-24" data-testid={`category-${cat.slug}`}>
              <div className="mb-6">
                <h2 className="text-2xl md:text-3xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  {cat.name}
                </h2>
                <p className="text-sm text-slate-500">{cat.description} <span className="font-mono text-slate-400">{catPages.length} guides</span></p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {catPages.map(page => (
                  <Link
                    key={page.slug}
                    to={`/guides/${page.slug}`}
                    className="group bg-white rounded-2xl p-5 card-interactive"
                    data-testid={`guide-link-${page.slug}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                      <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">{cat.name}</span>
                    </div>
                    <h3 className="text-base font-medium text-slate-900 mb-1 group-hover:text-emerald-700 transition-colors" style={{ fontFamily: "'Instrument Serif', serif" }}>
                      {page.title}
                    </h3>
                    <p className="text-[12px] text-slate-500 leading-relaxed line-clamp-2">{page.subtitle}</p>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mesh-gradient-hero noise-overlay relative rounded-3xl overflow-hidden p-10 md:p-14 text-center">
            <div className="relative z-10 max-w-lg mx-auto">
              <h2 className="text-2xl md:text-3xl text-white tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Done reading? <span className="italic">Get matched.</span>
              </h2>
              <p className="text-sm text-white/40 mb-6">6 questions, 3 minutes, personalized neighbourhood scores.</p>
              <Link to="/blueprint">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-3 h-auto text-sm font-semibold hover:-translate-y-px transition-all shadow-xl">
                  Start My Blueprint <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
